import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import { makeRequest } from '../../api';
import { Options } from "../../interfaces/Options";
import { Match } from "../../interfaces/Match";
import { UnifiedResponse, UnifiedPair, UnifiedSide } from "../../interfaces/Unified";
import { HalfTimeRow } from "../HalfTime/HalfTimeRow";

interface MatchWithAvg {
    match: Match;
    avgTimer: string;
}

const timeToSeconds = (time: string) => {
    if (!time) return Infinity;
    const [minutes, seconds] = time.split(":").map(Number);
    return (minutes || 0) * 60 + (seconds || 0);
};

// Solo amistosos. El backend usa una lista fija de ligas ("Mundo - Amistosos",
// "... (F)", "... juveniles", "... Sub 19 (F)"). Aquí usamos el marcador
// "Amistoso" en el nombre de liga para cubrir esas y cualquier variante futura.
const isFriendly = (match: Match) => /amistoso/i.test(match.league || '');

// Timer "activo" de un lado según su fase: en 2T manda sHTimer, en 1T htTimer.
const activeTimer = (side: UnifiedSide) => (side.ht ? side.sHTimer : side.htTimer);
const activeTimerSec = (side: UnifiedSide) => {
    const s = timeToSeconds(activeTimer(side));
    return s === Infinity ? -1 : s;
};

// Un par difiere si cambia la fase, si el marcador difiere, o si los minutos se
// separan más de 2 (el timer de Melbet se congela en 90:00, el de Novibet cuenta
// el alargue, así que pequeñas diferencias son esperables).
const pairDiffers = (pair: UnifiedPair) => {
    const { htDiffers, minuteDiff, scoreDiffers } = pair.discrepancies;
    return htDiffers || scoreDiffers || (minuteDiff !== null && Math.abs(minuteDiff) > 2);
};

// Grupo 1 (Tier 1): Fin PT (htTimer) más cerca del 45:00 => primer tiempo "limpio".
const HT_TIER1 = 45 * 60 + 10;   // 45:10
// Grupo 2 (Goleadas): diferencia de goles al HT.
const GOLEADA_DIF = 2;

type Group = 1 | 2 | 3;
const GROUPS: { id: Group; label: string; bg: string }[] = [
    { id: 1, label: '🔥 Tier 1 — Fin PT < 45:10 (más cerca de 45:00) · por Timer ↓', bg: '#b9f6ca' },
    { id: 2, label: '💥 Goleadas — difGoals ≥ 2 · por Timer ↓',                       bg: '#ffe0b2' },
    { id: 3, label: 'Otros amistosos · por Timer ↓',                                   bg: 'transparent' },
];

// Tier 1 tiene prioridad: un partido "limpio" no se repite en goleadas aunque
// también tenga difGoals ≥ 2.
function groupOf(row: MatchWithAvg): Group {
    const htSec = row.match.htTimer ? timeToSeconds(row.match.htTimer) : Infinity;
    if (htSec < HT_TIER1) return 1;
    if ((row.match.difGoals ?? 0) >= GOLEADA_DIF) return 2;
    return 3;
}

const FriendlyTable = () => {
    const [data, setData] = useState<MatchWithAvg[]>([]);
    const [unified, setUnified] = useState<UnifiedResponse | null>(null);
    const [leagueFilter, setLeagueFilter] = useState<string>('all');
    const [updatedAt, setUpdatedAt] = useState<string>('');

    const fetchData = useCallback(async () => {
        const options: Options = { method: 'get' };
        // Ambos fetches en el mismo ciclo. allSettled para que un fallo del
        // endpoint unified no tumbe los amistosos (y viceversa).
        const [shResult, unifiedResult] = await Promise.allSettled([
            makeRequest('api/get_sh_potential_matches', options),
            makeRequest('api/matches/unified', options),
        ]);
        if (shResult.status === 'fulfilled' && shResult.value.status === 200) {
            setData((shResult.value.data as MatchWithAvg[]).filter(d => isFriendly(d.match)));
        }
        if (unifiedResult.status === 'fulfilled' && unifiedResult.value.status === 200) {
            setUnified(unifiedResult.value.data as UnifiedResponse);
        }
        setUpdatedAt(new Date().toLocaleTimeString());
    }, []);

    useEffect(() => {
        fetchData();
        const id = setInterval(fetchData, 30000); // auto-refresh
        return () => clearInterval(id);
    }, [fetchData]);

    // Map de par por matchID de Novibet, para cruzar cada fila con su Melbet.
    const pairsByNovibetID = useMemo(() => {
        const map = new Map<string, UnifiedPair>();
        for (const pair of unified?.pairs ?? []) {
            map.set(pair.novibet.matchID, pair);
        }
        return map;
    }, [unified]);

    // Solo-Melbet filtrado a amistosos y ordenado por timer activo desc.
    const melbetOnlyFriendlies = useMemo(() => {
        const list = (unified?.melbetOnly ?? []).filter(m => /friendl|amistos/i.test(m.league || ''));
        return [...list].sort((a, b) => activeTimerSec(b) - activeTimerSec(a));
    }, [unified]);

    // Fin PT de un partido Melbet: el ingest congela htTimer en el último valor
    // de 1T visto. '00:00' significa "visto recién en 2T" (desconocido); en 1T
    // aún no hay Fin PT. En ambos casos → Infinity (no clasifica a Tier 1).
    const melbetFinPtSec = (m: UnifiedSide) => {
        if (!m.ht || !m.htTimer) return Infinity;
        const s = timeToSeconds(m.htTimer);
        return s <= 0 ? Infinity : s;
    };
    const melbetDif = (m: UnifiedSide) => Math.abs((m.scores?.[0] ?? 0) - (m.scores?.[1] ?? 0));
    // Mismos tiers que la tabla Novibet: Tier 1 fin PT limpio, Goleadas, Otros.
    const melbetGroupOf = (m: UnifiedSide): Group => {
        if (melbetFinPtSec(m) < HT_TIER1) return 1;
        if (melbetDif(m) >= GOLEADA_DIF) return 2;
        return 3;
    };
    const melbetGroups = useMemo(() => {
        const buckets: Record<Group, UnifiedSide[]> = { 1: [], 2: [], 3: [] };
        for (const m of melbetOnlyFriendlies) buckets[melbetGroupOf(m)].push(m);
        return buckets;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [melbetOnlyFriendlies]);

    const leagues = useMemo(() => {
        const uniqueLeagues = Array.from(new Set(data.map(d => d.match.league)));
        return uniqueLeagues.sort();
    }, [data]);

    const filteredData = useMemo(() => {
        return data.filter(({ match }) => {
            if (leagueFilter !== 'all' && match.league !== leagueFilter) return false;
            return true;
        });
    }, [data, leagueFilter]);

    // Orden dentro de cada grupo: por Timer (sHTimer) DESCENDENTE, para que el
    // partido a punto de terminar quede arriba. Timer ausente => al fondo.
    const timerSec = (t: string) => {
        const s = timeToSeconds(t);
        return s === Infinity ? -1 : s;
    };
    const sortRows = (rows: MatchWithAvg[]) => {
        return [...rows].sort((a, b) => timerSec(b.match.sHTimer) - timerSec(a.match.sHTimer));
    };

    const groups = useMemo(() => {
        const buckets: Record<Group, MatchWithAvg[]> = { 1: [], 2: [], 3: [] };
        for (const row of filteredData) buckets[groupOf(row)].push(row);
        return buckets;
    }, [filteredData]);

    // Celda "Melbet" de una fila: link a Melbet si hay par, y si difiere, lo que
    // dice Melbet (fase / timer / marcador) resaltado; "—" si no hay par.
    const renderMelbetCell = (novibetMatchID: string): React.ReactNode => {
        const pair = pairsByNovibetID.get(novibetMatchID);
        if (!pair) return '—';
        const { melbet } = pair;
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <a href={melbet.link} target="_blank" rel="noopener noreferrer">Melbet ↗</a>
                {pairDiffers(pair) && (
                    <span style={{ color: '#d84315', fontSize: 11, fontWeight: 600 }}>
                        {(melbet.ht ? '2T' : '1T')} {activeTimer(melbet)} {melbet.scores.join('-')}
                    </span>
                )}
            </span>
        );
    };

    const tableHeaders = (
        <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Liga</TableCell>
            <TableCell>Partido</TableCell>
            <TableCell>Fin PT</TableCell>
            <TableCell>Timer</TableCell>
            <TableCell>Tot Goles</TableCell>
            <TableCell>Diff Goles</TableCell>
            <TableCell>Under</TableCell>
            <TableCell>Avg Timer</TableCell>
            <TableCell>Link</TableCell>
            <TableCell>Melbet</TableCell>
        </TableRow>
    );

    return (
        <div>
            <h1>Amistosos — Tier 1 (Fin PT ≈ 45:00) y goleadas</h1>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', margin: '10px 0', flexWrap: 'wrap' }}>
                <Button variant="contained" color="primary" onClick={fetchData}>Refresh</Button>
                <FormControl style={{ minWidth: 200 }}>
                    <InputLabel>Liga</InputLabel>
                    <Select value={leagueFilter} label="Liga" onChange={(e) => setLeagueFilter(e.target.value)}>
                        <MenuItem value="all">Todas</MenuItem>
                        {leagues.map(league => (
                            <MenuItem key={league} value={league}>{league}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <span style={{ color: '#888' }}>
                    {filteredData.length} amistosos · T1:{groups[1].length} · Goleadas:{groups[2].length} · Otros:{groups[3].length} · Melbet-only:{melbetOnlyFriendlies.length} · act. {updatedAt}
                </span>
            </div>

            {GROUPS.map(g => {
                const rows = sortRows(groups[g.id]);
                if (rows.length === 0) return null;
                return (
                    <div key={g.id} style={{ marginBottom: 24 }}>
                        <h2 style={{ background: g.bg, padding: '6px 10px', borderRadius: 4, marginBottom: 8 }}>
                            {g.label} <span style={{ color: '#666', fontWeight: 'normal' }}>({rows.length})</span>
                        </h2>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>{tableHeaders}</TableHead>
                                <TableBody>
                                    {rows.map(({ match, avgTimer }) => (
                                        <HalfTimeRow
                                            key={match.matchID}
                                            match={match}
                                            avgTimer={avgTimer}
                                            extraCell={renderMelbetCell(match.matchID)}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                );
            })}

            {melbetOnlyFriendlies.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <h2 style={{ background: '#e1f5fe', padding: '6px 10px', borderRadius: 4, marginBottom: 8 }}>
                        Solo Melbet (amistosos) <span style={{ color: '#666', fontWeight: 'normal' }}>({melbetOnlyFriendlies.length})</span>
                    </h2>
                    {GROUPS.map(g => {
                        const rows = melbetGroups[g.id];
                        if (rows.length === 0) return null;
                        return (
                            <div key={`mb-${g.id}`} style={{ marginBottom: 16 }}>
                                <h3 style={{ background: g.bg, padding: '4px 10px', borderRadius: 4, marginBottom: 6 }}>
                                    {g.label} <span style={{ color: '#666', fontWeight: 'normal' }}>({rows.length})</span>
                                </h3>
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>ID</TableCell>
                                                <TableCell>Liga</TableCell>
                                                <TableCell>Partido</TableCell>
                                                <TableCell>Fase</TableCell>
                                                <TableCell>Fin PT</TableCell>
                                                <TableCell>Timer</TableCell>
                                                <TableCell>Tot Goles</TableCell>
                                                <TableCell>Diff Goles</TableCell>
                                                <TableCell>Link</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.map(m => (
                                                <TableRow key={m.matchID}>
                                                    <TableCell>{m.matchID}</TableCell>
                                                    <TableCell>{m.league}</TableCell>
                                                    <TableCell>{m.competitors.join(' vs ')}</TableCell>
                                                    <TableCell>{m.ht ? '2T' : '1T'}</TableCell>
                                                    <TableCell>{melbetFinPtSec(m) !== Infinity ? m.htTimer : '—'}</TableCell>
                                                    <TableCell>{activeTimer(m)}</TableCell>
                                                    <TableCell>{m.totGoals}</TableCell>
                                                    <TableCell>{melbetDif(m)}</TableCell>
                                                    <TableCell>
                                                        <a href={m.link} target="_blank" rel="noopener noreferrer">Melbet ↗</a>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export { FriendlyTable };
